import React, { lazy, useEffect, useMemo } from 'react';
import { Suspense } from 'react';
import { matchPath, Redirect, Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { CurrentViewPropertySessionProvider } from 'src/hooks/use-current-view-property-session';
import { useViewer } from 'src/hooks/use-viewer';
import Loading from '~components/loading';
import AuthPageLayout from '~containers/auth-page-layout';
import { SideNavigationOpenContextProvider } from '~containers/auth-page-layout/side-navigation-context';
import ErrorBoundary from '~containers/common-pages/error-boundary';
import { ReleaseHintProvider } from '~containers/release-hint/release-hint-provider';
import { routes } from '~containers/router/routes';
import { OPERATIONS } from '~graphql-types';

const ChooseRole = lazy(() =>
  import(/* webpackChunkName: "ChooseRole" */ '~containers/choose-role')
);
const Page401 = lazy(() =>
  import(/* webpackChunkName: "Page401" */ '~containers/common-pages/page-401')
);
const Page404 = lazy(() =>
  import(/* webpackChunkName: "Page404" */ '~containers/common-pages/page-404')
);
const PageComingSoon = lazy(() =>
  import(/* webpackChunkName: "PageComingSoon" */ '~containers/common-pages/page-coming-soon')
);

const findPermission = (permissions, ...slugs) => {
  let result;
  let temp = permissions;
  for (let i = 0; i < slugs.length; i++) {
    if (!temp) {
      result = null;
      break;
    }
    const permission = temp.find((item) => item.slug === slugs[i]);
    temp = permission?.childPermissions;
    result = permission;
  }
  return result;
};

const getBreadcrumbs = (url) => {
  const paths = url.split('/').slice(1);

  if (paths[0] === 'settings' && paths.length > 1) {
    return paths.map((label, index) => {
      let path = `/${paths.slice(0, index + 1).join('/')}`;
      if (index === 1) path = `/${paths[0]}#${paths[1]}`;
      if (index === paths.length - 1) path = null;
      return { label, path };
    });
  }

  if (paths[0] === 'student' && paths.length > 1) {
    return paths.map((label, index) => ({
      label,
      path:
        index === 1 || index === paths.length - 1
          ? null
          : `/${paths.slice(0, index + 1).join('/')}`,
    }));
  }

  return paths.map((label, index) => ({
    label,
    path: index === paths.length - 1 ? null : `/${paths.slice(0, index + 1).join('/')}`,
  }));
};

const HomePageContent = (props) => {
  const history = useHistory();
  const location = useLocation();
  const { currentRole } = useViewer();
  const { url, Component, permission } = props;

  useEffect(() => {
    const hasRole = !!currentRole;
    if (!hasRole) history.push('/choose-role');
  }, [history, currentRole]);

  useEffect(() => {
    if (!permission?.operations.includes(OPERATIONS.READ)) {
      history.replace('/401');
    }
  }, [history, permission?.operations]);

  return (
    <Component
      breadcrumbs={getBreadcrumbs(url)}
      history={history}
      location={location}
      operations={permission?.operations}
      routes={permission}
    />
  );
};

function HomePage() {
  const location = useLocation();
  const history = useHistory();
  const viewer = useViewer();

  useEffect(() => {
    if (viewer) {
      const hasRole = !!viewer.currentRole;
      const atChooseRolePage = matchPath(location.pathname, '/choose-role');
      if (!hasRole && !atChooseRolePage) {
        history.push(
          '/choose-role',
          atChooseRolePage ? undefined : { locationAfterChoosingRole: location }
        );
      }
    }
  }, [history, location, viewer]);

  const routeComponents = useMemo(
    () =>
      Object.entries(routes).map(([urlPath, { Component, slugs }]) => (
        <Route key={urlPath} exact path={urlPath}>
          <ErrorBoundary>
            <HomePageContent
              url={urlPath}
              Component={Component}
              permission={findPermission(viewer.currentRole.role.permissions, ...slugs)}
            />
          </ErrorBoundary>
        </Route>
      )),
    [viewer.currentRole.role.permissions]
  );

  return (
    <Switch>
      <Route exact path="/choose-role">
        <ChooseRole />
      </Route>

      <Route path="/">
        <SideNavigationOpenContextProvider>
          <ReleaseHintProvider>
            <CurrentViewPropertySessionProvider>
              <AuthPageLayout>
                <Suspense fallback={<Loading />}>
                  <Switch>
                    <Route path="/401">
                      <Page401 />
                    </Route>

                    <Route path="/404">
                      <Page404 />
                    </Route>

                    <Route path="/" exact>
                      <Redirect to="/dashboard" />
                    </Route>

                    <Route exact path="/settings/admin/roles">
                      <PageComingSoon />
                    </Route>

                    <Route exact path="/dashboard/reminder">
                      <PageComingSoon />
                    </Route>

                    {routeComponents}

                    <Redirect to="/404" />
                  </Switch>
                </Suspense>
              </AuthPageLayout>
            </CurrentViewPropertySessionProvider>
          </ReleaseHintProvider>
        </SideNavigationOpenContextProvider>
      </Route>
    </Switch>
  );
}

export default HomePage;
